import React from 'react'
import { Collapse, Tag, Typography, Divider } from 'antd'
import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'

const { Text } = Typography

const VALIDATION_TITLES: Record<string, string> = {
  global_score: '📊 Score Global',
  identity: '🆔 Validación Identidad',
  criminal: '⚖️ Antecedentes Penales',
  legal_processes: '📋 Procesos Legales',
  insolvency: '💰 Insolvencia',
  fiscal: '💼 Responsabilidad Fiscal',
  pep: '👔 Persona Expuesta Políticamente',
  seas_embargos: '🚫 Embargos SEAS',
  risk_policy: '📈 Política de Riesgo',
  restrictive_lists: '📝 Listas Restrictivas',
}

interface ValidationRule {
  description: string
  rules: string[]
}

const VALIDATION_RULES: Record<string, ValidationRule> = {
  global_score: {
    description: 'Evaluación del puntaje global de confianza Truora',
    rules: [
      'Puntaje 9–10 → Alta confianza (APROBADO)',
      'Puntaje ≤8 → Requiere revisión detallada (PENDING_REVIEW)',
    ],
  },
  identity: {
    description: 'Validación de identidad contra base de datos Registraduría Nacional',
    rules: [
      'Cédula debe estar vigente',
      'Nombre completo debe coincidir con el registro',
      'Número de identificación debe ser válido y activo',
      'Inconsistencias → PENDING_REVIEW (requiere análisis de fábrica)',
    ],
  },
  criminal: {
    description: 'Verificación de antecedentes en Policía Nacional y Procuraduría General',
    rules: [
      'Sin antecedentes → APROBADO',
      'Antecedentes activos → RECHAZO automático',
      '"Antes Buscado" → PENDING_REVIEW (requiere validación de analista)',
    ],
  },
  legal_processes: {
    description:
      'Análisis de procesos jurídicos donde el cliente aparece como DEMANDADO / INDICIADO / CAUSANTE',
    rules: [
      'Embargos abiertos detectados por keywords en actuaciones procesales → RECHAZO',
      'Procesos en estado Privado → Requieren validación documental (WARNING)',
      'Sin embargos ni procesos bloqueantes → APROBADO',
    ],
  },
  insolvency: {
    description: 'Consulta en centros de conciliación por procesos de insolvencia',
    rules: [
      'Rol DEUDOR en proceso de insolvencia → RECHAZO automático',
      'Rol CONVOCANTE / CONVOCADO / ACREEDOR → Requiere documentación (WARNING)',
      'Sin procesos de insolvencia → APROBADO',
    ],
  },
  fiscal: {
    description: 'Consulta Contraloría General de la República por responsabilidad fiscal',
    rules: [
      'Registrado con responsabilidad fiscal activa → RECHAZO (LÍMITE PROCESOS JURÍDICOS)',
      'Sin responsabilidades fiscales → APROBADO',
    ],
  },
  pep: {
    description: 'Verificación de Persona Expuesta Políticamente (Compliance SARLAFT)',
    rules: [
      'Cliente identificado como PEP → PENDING_REVIEW (requiere validación área Compliance)',
      'No PEP → APROBADO',
    ],
  },
  seas_embargos: {
    description: 'Banderas de embargo pre-computadas por SEAS (campo analisis_riesgo)',
    rules: [
      'cliente_Embargado = SI → RECHAZO automático (embargo activo vigente)',
      'embargo_Centrales = SI → Advertencia, requiere revisión del analista',
      'historial_Embargos: conteo de embargos históricos (cerrados) — se evalúa en Política de Riesgo (4.2.13)',
    ],
  },
  risk_policy: {
    description: 'Aplicación de política crediticia PO-GR01 V12 (26/03/2026) con datos Experian',
    rules: [
      '── PENSIONADO / PROPIEDAD ──',
      '4.2.1 — Score 1–300 → máx $30M, sin restricciones',
      '4.2.2 — Score 301–420 → máx $100M, sin restricciones',
      '4.2.3 — Colpensiones score EXACTAMENTE 150 → Negado',
      '4.2.3 — Colpensiones score 301–420 → $100M, máx 4 compras de cartera',
      '4.2.4 — Fiduprevisora score EXACTAMENTE 150 → Negado',
      '4.2.4 — Fiduprevisora score 301–420 → $100M, máx 4 compras de cartera',
      '4.2.5 — FOPEP → máx 3 compras de cartera (sin restricción de score)',
      '4.2.6 — Mindefensa → máx 2 compras de cartera (sin restricción de score)',
      '4.2.7 — CASUR score <400 → Negado',
      '4.2.8 — Colfondos score 301–420 → $100M libre inversión; score >420 → $180M libre inversión',
      '4.2.9 — Protección score 301–420 → $50M',
      '4.2.10 — Asulado score 301–420 → $50M',
      '4.2.11 — Alfa score 301–420 → $50M, máx 1 compra de cartera',
      '4.2.12 — Pensionado radicado/nacido/viviendo en Barranquilla con score <400 → cap $20M',
      '4.2.13 — Historial >4 embargos cerrados + score >420 → cap $25M',
      'Edad 76–80 años → cap $50M (Política Plazo y Asegurabilidad)',
      'Edad >80a10m → Negado (Política Plazo y Asegurabilidad)',
      '── DOCENTE ──',
      '4.3.1 — Score <200 → Negado',
      '4.3.2 — Score 200–249 → $30M, máx 4 compras de cartera',
      '4.3.3 — Score 250–300 → $30M, sin restricciones',
      '4.3.4 — Score 301–649 → $80M, sin restricciones',
      '4.3.5 — SED Bolívar score <650 → Negado; score ≥650 → $80M',
      '4.3.6 — SED Córdoba score ≥420 → $80M',
      '4.3.7 — Grupo A (SEM Cali, SEM Barranquilla, SED Huila, SEM Cartagena, SEM Sincelejo, SED Cauca, SED Norte de Santander, SED Cundinamarca, SEM Valledupar, SED Antioquia) score <380 → Negado',
      '4.3.8 — Grupo B (SEM Magangué, SED Atlántico, SED Santander, SED Cesar, SED Sahagún) score <420 → Negado',
      '4.3.9 — Grupo C (SEM Villavicencio, SEM Neiva, SEM Medellín, SEM Popayán, SED Magdalena, SED Córdoba) score 301–420 → $30M',
      '4.3.10 — Docente radicado en municipio del Atlántico o nacido/viviendo en Barranquilla: score <420 → Negado',
      'Edad 65–66 años → cap $150M (Política Plazo y Asegurabilidad)',
      'Edad >66a6m → Negado (Política Plazo y Asegurabilidad)',
      '── GENERAL ──',
      'Saldo mora Experian >$70M → Negado (PO-FC01)',
    ],
  },
  restrictive_lists: {
    description:
      'Verificación contra listas restrictivas internas, OFAC, ONU y otras sanciones internacionales',
    rules: [
      'Cliente en lista bloqueante (riesgo CRITICAL/HIGH) → RECHAZO automático',
      'Coincidencias de riesgo medio → PENDING_REVIEW (revisión humana requerida)',
      'Sin coincidencias → APROBADO',
    ],
  },
}

const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'PASSED': return 'success'
    case 'FAILED': return 'error'
    case 'WARNING': return 'warning'
    default: return 'default'
  }
}

const getStatusLabel = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'PASSED': return 'Aprobado'
    case 'FAILED': return 'Rechazado'
    case 'WARNING': return 'Advertencia'
    default: return status || 'Pendiente'
  }
}

const getStatusIcon = (status: string) => {
  const s = status?.toUpperCase()
  if (s === 'PASSED') return <CheckOutlined style={{ color: '#52c41a', fontSize: 18 }} />
  if (s === 'FAILED') return <CloseOutlined style={{ color: '#f5222d', fontSize: 18 }} />
  if (s === 'WARNING') return <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
  return <ClockCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />
}

const formatCOP = (amount: number) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(amount)

const BoolTag: React.FC<{
  value: boolean
  trueLabel: string
  falseLabel: string
  trueColor?: string
  falseColor?: string
}> = ({ value, trueLabel, falseLabel, trueColor = 'error', falseColor = 'success' }) => (
  <Tag color={value ? trueColor : falseColor}>{value ? trueLabel : falseLabel}</Tag>
)

const DataField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: 13, flexWrap: 'wrap' }}>
    <Text type="secondary" style={{ minWidth: 170, flexShrink: 0 }}>
      {label}:
    </Text>
    <span>{children}</span>
  </div>
)

const renderValidationData = (key: string, validation: any): React.ReactNode => {
  const details = validation.details || {}

  switch (key) {
    case 'global_score':
      return (
        <>
          <DataField label="Puntaje Truora">
            <Text strong>{validation.score ?? details.score ?? 'N/A'}</Text>
          </DataField>
          <DataField label="Nivel de Confianza">
            <Tag
              color={
                (validation.trust_level || details.trust_level) === 'HIGH' ? 'success' : 'warning'
              }
            >
              {validation.trust_level || details.trust_level || 'N/A'}
            </Tag>
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
        </>
      )

    case 'identity':
      return (
        <>
          <DataField label="Estado Cédula">{details.document_status || 'N/A'}</DataField>
          <DataField label="Identidad Verificada">
            <BoolTag
              value={!details.verified}
              trueLabel="No verificada"
              falseLabel="Verificada"
            />
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
        </>
      )

    case 'criminal':
      return (
        <>
          <DataField label="Antecedentes Activos">
            <BoolTag
              value={validation.has_record ?? details.has_record ?? false}
              trueLabel="Sí — antecedentes encontrados"
              falseLabel="No"
            />
          </DataField>
          <DataField label='Estado "Antes Buscado"'>
            <BoolTag
              value={validation.antes_buscado ?? details.antes_buscado ?? false}
              trueLabel="Detectado"
              falseLabel="No"
              trueColor="warning"
            />
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
        </>
      )

    case 'legal_processes': {
      const embargos: any[] = details.embargos || []
      return (
        <>
          <DataField label="Procesos como demandado">
            <Text strong>
              {validation.total_processes ?? details.total_processes ?? 0}
            </Text>
          </DataField>
          <DataField label="Embargos Abiertos">
            <Text
              strong
              style={{
                color:
                  (validation.open_embargos_count ?? details.open_embargos_count ?? 0) > 0
                    ? '#f5222d'
                    : '#52c41a',
              }}
            >
              {validation.open_embargos_count ?? details.open_embargos_count ?? 0}
            </Text>
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
          {embargos.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text
                type="secondary"
                style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}
              >
                Embargos Detectados
              </Text>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12, color: '#555' }}>
                {embargos.map((e: any, i: number) => (
                  <li key={i}>{e.tipo_proceso || e.tipo_sujeto || JSON.stringify(e)}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )
    }

    case 'insolvency':
      return (
        <>
          <DataField label="Es Deudor en Insolvencia">
            <BoolTag
              value={validation.is_debtor ?? details.is_debtor ?? false}
              trueLabel="Sí — registrado como DEUDOR"
              falseLabel="No"
            />
          </DataField>
          <DataField label="Requiere Documentación">
            <BoolTag
              value={validation.requires_document ?? details.requires_document ?? false}
              trueLabel="Sí"
              falseLabel="No"
              trueColor="warning"
            />
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
        </>
      )

    case 'fiscal':
      return (
        <>
          <DataField label="Responsabilidad Fiscal (Contraloría)">
            <BoolTag
              value={validation.is_responsible ?? details.is_responsible ?? false}
              trueLabel="Reportado"
              falseLabel="Sin responsabilidades"
            />
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
        </>
      )

    case 'pep': {
      const matches: any[] = validation.matches || details.matches || []
      return (
        <>
          <DataField label="Es Persona Políticamente Expuesta">
            <BoolTag
              value={validation.is_pep ?? details.is_pep ?? false}
              trueLabel="Sí — PEP detectado"
              falseLabel="No es PEP"
              trueColor="warning"
            />
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
          {matches.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text
                type="secondary"
                style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}
              >
                Coincidencias PEP
              </Text>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12, color: '#555' }}>
                {matches.map((m: any, i: number) => (
                  <li key={i}>{typeof m === 'string' ? m : JSON.stringify(m)}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )
    }

    case 'seas_embargos': {
      const hist = details.historial_embargos ?? 0
      return (
        <>
          <DataField label="Cliente Embargado (SEAS)">
            <BoolTag value={!!details.cliente_embargado} trueLabel="Sí" falseLabel="No" />
          </DataField>
          <DataField label="Embargo en Centrales">
            <BoolTag
              value={!!details.embargo_centrales}
              trueLabel="Sí"
              falseLabel="No"
              trueColor="warning"
            />
          </DataField>
          <DataField label="Historial de Embargos">
            <Text strong style={{ color: hist > 4 ? '#faad14' : undefined }}>
              {hist}
            </Text>
            {hist > 4 && (
              <Tag color="orange" style={{ marginLeft: 8 }}>
                &gt;4 — aplica límite PO-GR01 4.2.13
              </Tag>
            )}
          </DataField>
          {details.message && <DataField label="Resultado">{details.message}</DataField>}
          {(validation.reason || details.reason) && (
            <DataField label="Razón">{validation.reason || details.reason}</DataField>
          )}
        </>
      )
    }

    case 'risk_policy': {
      const approved = validation.approved ?? details.approved
      const maxAmount = validation.max_amount ?? details.max_amount
      const reason = validation.reason ?? details.reason
      const rejectionReason = validation.rejection_reason ?? details.rejection_reason
      const restrictions: string[] = validation.restrictions ?? details.restrictions ?? []
      return (
        <>
          <DataField label="Decisión Política">
            <BoolTag
              value={!approved}
              trueLabel="Rechazado por política"
              falseLabel="Aprobado por política"
            />
          </DataField>
          {maxAmount !== undefined && (
            <DataField label="Monto Máximo Autorizado">
              <Text strong style={{ color: '#1890ff', fontSize: 14 }}>
                {formatCOP(maxAmount)}
              </Text>
            </DataField>
          )}
          {(reason || rejectionReason) && (
            <DataField label="Regla Aplicada">
              <Text italic style={{ color: '#444' }}>
                {reason || rejectionReason}
              </Text>
            </DataField>
          )}
          {restrictions.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <Text
                type="secondary"
                style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}
              >
                Restricciones Aplicadas
              </Text>
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {restrictions.map((r: string, i: number) => (
                  <Tag key={i} color="orange">
                    {r}
                  </Tag>
                ))}
              </div>
            </div>
          )}
        </>
      )
    }

    case 'restrictive_lists': {
      const isBlocked = validation.is_blocked ?? details.is_blocked ?? false
      const requiresReview = validation.requires_manual_review ?? details.requires_manual_review ?? false
      const recommendation = validation.recommendation ?? details.recommendation
      return (
        <>
          <DataField label="Bloqueado en Listas">
            <BoolTag
              value={!!isBlocked}
              trueLabel="Bloqueado"
              falseLabel="No bloqueado"
            />
          </DataField>
          <DataField label="Requiere Revisión Manual">
            <BoolTag
              value={!!requiresReview}
              trueLabel="Requerida"
              falseLabel="No requerida"
              trueColor="warning"
            />
          </DataField>
          {recommendation && (
            <DataField label="Recomendación">
              <Text italic>{recommendation}</Text>
            </DataField>
          )}
          {details.message && <DataField label="Mensaje">{details.message}</DataField>}
        </>
      )
    }

    default:
      return (
        <>
          {Object.entries(details).map(([k, v]) => (
            <DataField key={k} label={k}>
              {String(v)}
            </DataField>
          ))}
        </>
      )
  }
}

const renderPanelBody = (key: string, validation: any): React.ReactNode => {
  const ruleSet = VALIDATION_RULES[key]
  return (
    <div style={{ padding: '4px 0' }}>
      <Text
        type="secondary"
        style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}
      >
        Datos Registrados
      </Text>
      <div style={{ marginTop: 8 }}>{renderValidationData(key, validation)}</div>

      {ruleSet && (
        <>
          <Divider style={{ margin: '14px 0' }} />
          <Text
            type="secondary"
            style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}
          >
            Reglas Evaluadas
          </Text>
          <div style={{ fontSize: 12, color: '#999', marginTop: 3, marginBottom: 8 }}>
            {ruleSet.description}
          </div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {ruleSet.rules.map((rule, i) => (
              <li
                key={i}
                style={{
                  fontSize: 12,
                  marginBottom: 3,
                  color: rule.startsWith('──') ? '#aaa' : '#555',
                  fontWeight: rule.startsWith('──') ? 600 : 400,
                  listStyle: rule.startsWith('──') ? 'none' : undefined,
                  marginLeft: rule.startsWith('──') ? -20 : undefined,
                }}
              >
                {rule}
              </li>
            ))}
          </ul>
        </>
      )}

      {validation.processing_time_ms && (
        <div style={{ marginTop: 14, fontSize: 11, color: '#ccc' }}>
          Tiempo de procesamiento: {validation.processing_time_ms} ms
        </div>
      )}
    </div>
  )
}

interface Props {
  validations: Record<string, any>
}

const ValidationCollapsePanel: React.FC<Props> = ({ validations }) => {
  const entries = Object.entries(validations).filter(
    ([key]) => key !== 'ai_model' && key !== 'llm_narrative'
  )

  const items = entries.map(([key, validation]) => ({
    key,
    label: (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {VALIDATION_TITLES[key] ?? key}
          </div>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag
              color={getStatusColor(validation.status)}
              style={{ fontSize: 12, padding: '1px 7px', margin: 0 }}
            >
              {getStatusLabel(validation.status)}
            </Tag>
            {validation.details?.message && (
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>
                {validation.details.message}
              </Text>
            )}
          </div>
        </div>
        <div style={{ marginLeft: 16, flexShrink: 0 }}>
          {getStatusIcon(validation.status)}
        </div>
      </div>
    ),
    children: renderPanelBody(key, validation),
  }))

  return (
    <Collapse
      items={items}
      accordion={false}
      size="small"
      style={{ background: 'transparent' }}
    />
  )
}

export default ValidationCollapsePanel
